import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';

const REPORT_PATH = '/methodology#report';

interface Props {
  children: ReactNode;
  language?: 'en' | 'fa';
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const isFa = this.props.language === 'fa';
      const reportHref =
        typeof window !== 'undefined' ? `${window.location.origin}${REPORT_PATH}` : REPORT_PATH;

      return (
        <div
          className="min-h-screen flex items-center justify-center p-6"
          style={{ backgroundColor: 'var(--bg-surface)' }}
        >
          <div
            className="w-full max-w-md border p-6 text-left rounded-none"
            style={{
              borderColor: 'var(--status-red)',
              borderWidth: '1px',
              backgroundColor: 'var(--bg-elevated)',
              borderRadius: '4px',
            }}
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="shrink-0" size={24} style={{ color: 'var(--status-red)' }} aria-hidden />
              <div>
                <h2 className="text-lg font-semibold font-sans leading-snug" style={{ color: 'var(--text-primary)' }}>
                  IRINTER
                </h2>
                <p className="text-sm font-sans mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {isFa ? 'مشکلی پیش آمد' : 'Something went wrong'}
                </p>
              </div>
            </div>
            <p className="text-sm font-sans mb-2" style={{ color: 'var(--text-secondary)' }}>
              {isFa
                ? 'این بخش بارگذاری نشد. داده‌ها و خروجی‌های شما تحت تأثیر نیستند.'
                : 'This section could not load. Your data and exports are unaffected.'}
            </p>
            <p className="text-sm font-sans mb-6" style={{ color: 'var(--text-secondary)' }}>
              {isFa
                ? 'خط لولهٔ داده ممکن است فعال باشد؛ مشکل در رابط کاربری است.'
                : 'The data pipeline may still be running; this is a UI error.'}
            </p>

            {this.state.error && process.env.NODE_ENV === 'development' && (
              <details className="mb-4 text-left">
                <summary className="text-xs cursor-pointer mb-2 font-sans" style={{ color: 'var(--text-muted)' }}>
                  {isFa ? 'جزئیات خطا (توسعه)' : 'Error details (development)'}
                </summary>
                <pre
                  className="text-xs p-3 overflow-auto max-h-40 font-mono rounded-none border"
                  style={{
                    color: 'var(--status-red)',
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border)',
                  }}
                >
                  {this.state.error.toString()}
                  {'\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={this.handleReset}
                className="inline-flex items-center gap-2 border px-4 py-2 text-sm font-sans uppercase tracking-wider transition-colors"
                style={{
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-surface)',
                }}
              >
                <RefreshCw size={16} />
                {isFa ? 'بارگذاری مجدد' : 'Reload page'}
              </button>
              <a
                href={reportHref}
                className="inline-flex items-center gap-2 border px-4 py-2 text-sm font-sans uppercase tracking-wider"
                style={{
                  borderColor: 'var(--border)',
                  color: 'var(--accent-blue)',
                  backgroundColor: 'transparent',
                }}
              >
                <ExternalLink size={16} />
                {isFa ? 'گزارش مشکل' : 'Report issue'}
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
